import type { ProjectType } from '@atlassian/jira-common-constants';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ASANA,
	CLICK_UP,
	CSV,
	JIRA_SOFTWARE,
	MONDAY,
	TRELLO,
	type ImportType,
	DEFAULT,
	JIRA_BUSINESS,
} from '../../constants/import-type';
import { ProjectTypes } from '../../constants/project';
import { CROSS_FLOW, EXISTING_PROJECT, type Source } from '../../constants/source';
import {
	DEFAULT_CONFIG,
	SELECT_PRODUCT_STEP,
	UPLOAD_CSV_STANDARD_STEP,
	UPLOAD_CSV_STEP,
	type StepComponent,
	type Step,
	SELECT_SOURCE_STEP,
} from '../../constants/steps';
import { API_FLOW, type ImporterFlow } from '../../types';
import { getAsanaSteps } from './asana';
import { getClickUpSteps } from './clickup';
import { getCSVSteps, getStandardisedCSVSteps } from './csv';
import { getBusinessToSoftwareSteps, getSoftwareToBusinessSteps } from './jira';
import { getMondaySteps } from './monday';
import { getTrelloSteps } from './trello';

type ProjectStepConfig = {
	importType: ImportType;
	isSiteAdmin?: boolean;
	source?: Source;
	skipProductStep?: boolean;
	supportedFlow?: ImporterFlow;
};

type StepConfigProps = ProjectStepConfig & { projectType: ProjectType | null };

const getDefaultStepConfig = (source?: Source, skipProductStep?: boolean): StepComponent[] =>
	skipProductStep
		? [
				{
					...DEFAULT_CONFIG,
					overrides: source === EXISTING_PROJECT ? {} : { overrideReturnStep: SELECT_PRODUCT_STEP },
					step: source === EXISTING_PROJECT ? UPLOAD_CSV_STANDARD_STEP : UPLOAD_CSV_STEP,
				},
			]
		: [
				{
					...DEFAULT_CONFIG,
					step: SELECT_PRODUCT_STEP,
				},
				{
					...DEFAULT_CONFIG,
					step: source === EXISTING_PROJECT ? UPLOAD_CSV_STANDARD_STEP : UPLOAD_CSV_STEP,
				},
			];

const getJWMStepConfig = ({
	importType,
	isSiteAdmin,
	source,
}: ProjectStepConfig): StepComponent[] => {
	switch (importType) {
		case CSV:
			return getCSVSteps(ProjectTypes.BUSINESS, source);
		case TRELLO:
			return getTrelloSteps(isSiteAdmin);
		case ASANA:
			return getAsanaSteps(isSiteAdmin, ProjectTypes.BUSINESS);
		case MONDAY:
			return getMondaySteps();
		case JIRA_SOFTWARE:
			return getSoftwareToBusinessSteps();
		case CLICK_UP:
			return getClickUpSteps();
		default:
			return getDefaultStepConfig(source, true);
	}
};

const getJSWStepConfig = ({
	importType,
	isSiteAdmin,
	source,
}: ProjectStepConfig): StepComponent[] => {
	switch (importType) {
		case CSV:
			return getCSVSteps(ProjectTypes.SOFTWARE, source);
		case TRELLO:
			return getTrelloSteps(isSiteAdmin);
		case ASANA:
			return getAsanaSteps(isSiteAdmin, ProjectTypes.SOFTWARE);
		case JIRA_BUSINESS:
			return getBusinessToSoftwareSteps();
		default:
			return getDefaultStepConfig(source, true);
	}
};

const getStandardizedStepConfig = ({
	importType,
	isSiteAdmin,
	source,
	supportedFlow,
}: StepConfigProps): StepComponent[] => {
	const isAPIFlow = supportedFlow === API_FLOW;
	if (isAPIFlow) {
		switch (importType) {
			case TRELLO:
				return getTrelloSteps(isSiteAdmin);
			case ASANA:
				return getAsanaSteps(isSiteAdmin);
			case MONDAY:
				return getMondaySteps();
			case JIRA_SOFTWARE:
				return getSoftwareToBusinessSteps();
			case JIRA_BUSINESS:
				return getBusinessToSoftwareSteps();
			case CLICK_UP:
				return getClickUpSteps();
			default:
				break;
		}
	}

	if ((importType === DEFAULT && source !== EXISTING_PROJECT) || source === CROSS_FLOW) {
		return [
			{
				...DEFAULT_CONFIG,
				step: SELECT_SOURCE_STEP,
			},
		];
	}

	return getStandardisedCSVSteps({ importType, source, isSiteAdmin });
};

export const getStepConfig = (props: StepConfigProps): StepComponent[] => {
	const { importType, projectType, isSiteAdmin, source, skipProductStep } = props;
	if (fg('enable-x2c-standardised-flow')) {
		return getStandardizedStepConfig(props);
	}

	switch (projectType) {
		case ProjectTypes.SOFTWARE:
			return getJSWStepConfig({ importType, isSiteAdmin, source });
		case ProjectTypes.BUSINESS:
			return getJWMStepConfig({ importType, isSiteAdmin, source });
		default:
			return getDefaultStepConfig(source, skipProductStep);
	}
};

export const getFirstStep = (props: StepConfigProps): Step => {
	const steps = getStepConfig(props);
	return steps[0].step;
};
